import { CompanyOutreachCell } from "./ui/CompanyOutreach";
import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Search, Building2, Linkedin, TriangleAlert, Plus, MessageSquare } from "lucide-react";
import Tooltip from "shared/Tooltip";
import PageHeader from "shared/PageHeader";
import StatusPill from "shared/StatusPill";
import Alert from "shared/Alert";
import Button from "shared/Button";
import EditCompanyModal from "./ui/EditCompanyModal";
import HoverCard from "./ui/HoverCard";
import { FILLED_INPUT, FORM_CONTROL, TABLE_SHELL, TBODY_ROW, TD, TH, THEAD_ROW } from "shared/tableStyles";
import PagerNav from "shared/PagerNav";
import { useUrlState, useUrlNumberState, useUrlBoolState, useUpdateEffect, rowLinkState, useHighlightedRow, rowDomId, ROW_HIGHLIGHT_CLASS } from "shared/listNavigation";
import { formatDate } from "./ui/crmUi";
import SortMenu, { type SortDir } from "./ui/SortMenu";
import { COMPANY_STATUS_LABELS } from "./companyStatus";
import { listPrimeCompanies, listCompanyCountries, listCompanyStates, getMissingAddressCount, MISSING_ADDRESS_QUERY, getOfficeNotLocatedCount, OFFICE_NOT_LOCATED_QUERY, getNaicsPendingCount, NAICS_PENDING_QUERY, type PrimeCompanyListItem } from "./api/CrmApi";

// Some canonical company LinkedIn values are a LinkedIn *search results* URL
// (e.g. /search/results/companies/?keywords=...) rather than the real company page.
const isLinkedinSearchUrl = (url: string) => /linkedin\.com\/(sales\/)?search(\/|\?|$)/i.test(url);

// Display labels only — the stored/API values stay "active" / "do_not_contact" / "not_a_fit" / "inactive".
const STATUS_LABELS = COMPANY_STATUS_LABELS;

function StatusBadge({ status }: { status: string }) {
  return (
    <StatusPill tone={status === "active" ? "success" : "neutral"} dot>
      {STATUS_LABELS[status] ?? status}
    </StatusPill>
  );
}

export default function CompaniesPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const crmBasePath = location.pathname.startsWith("/employee") ? "/employee/crm" : "/admin/crm";

  const [items, setItems] = useState<PrimeCompanyListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Page + filters live in the URL (?page=3&q=…) so the Back link from a
  // company page and the browser's back button both return to this exact view.
  const [page, setPage] = useUrlNumberState("page", 1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const [search, setSearch] = useUrlState("q", "");
  const [searchInput, setSearchInput] = useState(search);
  const [status, setStatus] = useUrlState("status", "");
  const [linkedinFilter, setLinkedinFilter] = useUrlState<"" | "yes" | "no">("linkedin", "");
  const [countryFilter, setCountryFilter] = useUrlState("country", ""); // "" = any, "none" = not set, else a country name
  const [stateFilter, setStateFilter] = useUrlState("state", ""); // an office in this state
  const [manuallyAdded, setManuallyAdded] = useUrlBoolState("manually_added");
  const [hasReplied, setHasReplied] = useUrlBoolState("has_replied");
  const [noRfp, setNoRfp] = useUrlBoolState("no_rfp");
  const [unconfirmed, setUnconfirmed] = useUrlBoolState("unconfirmed");
  const [addOpen, setAddOpen] = useState(false);
  const [missingAddress, setMissingAddress] = useUrlBoolState("missing_address");
  const [officeNotLocated, setOfficeNotLocated] = useUrlBoolState("office_not_located");
  const [serviceMissing, setServiceMissing] = useUrlBoolState("service_missing");
  const [credentialsMissing, setCredentialsMissing] = useUrlBoolState("credentials_missing");
  const [capabilityMissing, setCapabilityMissing] = useUrlBoolState("capability_missing");
  const [pastPerformanceMissing, setPastPerformanceMissing] = useUrlBoolState("past_performance_missing");
  const [naicsPending, setNaicsPending] = useUrlBoolState("naics_pending");
  const [ids, setIds] = useUrlState("ids", ""); // the research-gate list: comma-separated company ids
  const [naicsPendingCount, setNaicsPendingCount] = useState(0);
  const [service, setService] = useUrlState("service", "");
  const [serviceInput, setServiceInput] = useState(service);
  const [credential, setCredential] = useUrlState("credential", "");
  const [credentialInput, setCredentialInput] = useState(credential);
  const [missingAddressCount, setMissingAddressCount] = useState(0);
  const [notLocatedCount, setNotLocatedCount] = useState(0);
  const [sortBy, setSortBy] = useUrlState<"" | "recent_rfp_appearance">("sort", "recent_rfp_appearance"); // "" (cleared) = by name
  const [sortDir, setSortDir] = useUrlState<SortDir>("dir", "desc");
  const [countryOptions, setCountryOptions] = useState<string[]>([]);
  const [stateOptions, setStateOptions] = useState<string[]>([]);
  const highlightId = useHighlightedRow("company", !loading && items.length > 0);

  useEffect(() => {
    listCompanyCountries().then(setCountryOptions).catch(() => {
      // Non-fatal — the dropdown just offers "any" / "not set".
    });
    listCompanyStates().then(setStateOptions).catch(() => {
      // Non-fatal — the dropdown just offers "any".
    });
  }, []);

  useEffect(() => {
    getMissingAddressCount().then(setMissingAddressCount).catch(() => {
      // Non-fatal — the alert just doesn't show.
    });
    getOfficeNotLocatedCount().then(setNotLocatedCount).catch(() => {
      // Non-fatal — the alert just doesn't show.
    });
    getNaicsPendingCount().then(setNaicsPendingCount).catch(() => {
      // Non-fatal — the alert just doesn't show.
    });
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput), 300);
    return () => clearTimeout(t);
  }, [searchInput, setSearch]);

  useEffect(() => {
    const t = setTimeout(() => setService(serviceInput), 300);
    return () => clearTimeout(t);
  }, [serviceInput, setService]);

  useEffect(() => {
    const t = setTimeout(() => setCredential(credentialInput), 300);
    return () => clearTimeout(t);
  }, [credentialInput, setCredential]);

  // Skips the mount run — otherwise it would reset a page restored from the URL.
  useUpdateEffect(() => {
    setPage(1);
  }, [search, status, linkedinFilter, countryFilter, stateFilter, manuallyAdded, hasReplied, noRfp, unconfirmed, missingAddress, officeNotLocated, serviceMissing, credentialsMissing, capabilityMissing, pastPerformanceMissing, naicsPending, service, credential, ids, sortBy, sortDir]);

  useEffect(() => {
    setLoading(true);
    setError("");
    listPrimeCompanies({ page, page_size: 25, search: search || undefined, status: status || undefined, has_linkedin: linkedinFilter ? linkedinFilter === "yes" : undefined, country: countryFilter || undefined, state: stateFilter || undefined, manually_added: manuallyAdded || undefined, has_replied: hasReplied || undefined, no_rfp: noRfp || undefined, unconfirmed: unconfirmed || undefined, missing_address: missingAddress || undefined, office_not_located: officeNotLocated || undefined, service_missing: serviceMissing || undefined, credentials_missing: credentialsMissing || undefined, credential: credential || undefined, capability_missing: capabilityMissing || undefined, past_performance_missing: pastPerformanceMissing || undefined, ids: ids || undefined, naics_pending: naicsPending || undefined, service: service || undefined, sort_by: sortBy || undefined, sort_dir: sortDir })
      .then((res) => {
        setItems(res.items);
        setTotalCount(res.total_count);
        setTotalPages(res.total_pages);
      })
      .catch((e) => setError(e.response?.data?.detail || "Failed to load companies"))
      .finally(() => setLoading(false));
  }, [page, search, status, linkedinFilter, countryFilter, stateFilter, manuallyAdded, hasReplied, noRfp, unconfirmed, missingAddress, officeNotLocated, serviceMissing, credentialsMissing, capabilityMissing, pastPerformanceMissing, naicsPending, service, credential, ids, sortBy, sortDir]);

  return (
    <div className="flex flex-col gap-4 h-full min-h-0">
      {addOpen && (
        <EditCompanyModal
          onClose={() => setAddOpen(false)}
          onSaved={(created) => navigate(`${crmBasePath}/companies/${created.id}`)}
        />
      )}
      <PageHeader
        title="Prime Companies"
        badge={<StatusPill tone="neutral" size="sm">{totalCount} compan{totalCount === 1 ? "y" : "ies"}</StatusPill>}
        actions={
          <Button variant="primary" size="sm" onClick={() => setAddOpen(true)} title="Add a company by hand">
            <Plus className="w-3.5 h-3.5" /> Add company
          </Button>
        }
        description="Canonical, deduplicated companies resolved from prime contacts by email domain — click one to see everyone tagged under it and its capability lane."
      >
        {ids && (
          <Alert tone="info" role="status" className="flex items-center gap-3 flex-wrap">
            <span className="flex-1" data-testid="research-list-banner">
              Showing the <span className="font-semibold">research list</span> for an RFP ({ids.split(",").filter(Boolean).length} compan{ids.split(",").filter(Boolean).length === 1 ? "y" : "ies"}) — fill in what is missing on each company, then tell the assistant you are done.
            </span>
            <button type="button" onClick={() => setIds("")} className="font-medium underline">
              Clear
            </button>
          </Alert>
        )}
        {missingAddressCount > 0 && (
          <Alert tone="warning" role="status" className="flex items-center gap-3 flex-wrap">
            <TriangleAlert className="w-4 h-4 shrink-0" />
            <span className="flex-1">
              <span className="font-semibold">{missingAddressCount}</span> compan{missingAddressCount === 1 ? "y has" : "ies have"} no address set — research and fix {missingAddressCount === 1 ? "its" : "their"} address.
            </span>
            {!missingAddress && (
              <Link to={`${crmBasePath}/companies?${MISSING_ADDRESS_QUERY}`} className="font-medium text-primary hover:underline whitespace-nowrap">
                Fix Now
              </Link>
            )}
          </Alert>
        )}
        {notLocatedCount > 0 && (
          <Alert tone="warning" role="status" className="flex items-center gap-3 flex-wrap">
            <TriangleAlert className="w-4 h-4 shrink-0" />
            <span className="flex-1">
              <span className="font-semibold">{notLocatedCount}</span> compan{notLocatedCount === 1 ? "y has" : "ies have"} an office that could not be placed on the map (no usable city and state, or zip) — add the missing details so radius checks can use {notLocatedCount === 1 ? "it" : "them"}.
            </span>
            {!officeNotLocated && (
              <Link to={`${crmBasePath}/companies?${OFFICE_NOT_LOCATED_QUERY}`} className="font-medium text-primary hover:underline whitespace-nowrap">
                Fix Now
              </Link>
            )}
          </Alert>
        )}
        {naicsPendingCount > 0 && (
          <Alert tone="warning" role="status" className="flex items-center gap-3 flex-wrap">
            <TriangleAlert className="w-4 h-4 shrink-0" />
            <span className="flex-1">
              <span className="font-semibold">{naicsPendingCount}</span> compan{naicsPendingCount === 1 ? "y has" : "ies have"} NAICS suggestions waiting for your approval — they are not used by any RFP check until approved.
            </span>
            {!naicsPending && (
              <Link to={`${crmBasePath}/companies?${NAICS_PENDING_QUERY}`} className="font-medium text-primary hover:underline whitespace-nowrap">
                Review
              </Link>
            )}
          </Alert>
        )}
      </PageHeader>

      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative">
          <Search className="w-4 h-4 text-muted-foreground absolute left-2.5 top-1/2 -translate-y-1/2" />
          <input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search name or domain..."
            className={`w-64 pl-8 pr-3 py-1.5 h-9 ${FILLED_INPUT}`}
          />
        </div>
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className={`px-3 py-1.5 h-9 ${FORM_CONTROL}`}
        >
          <option value="">Status: any</option>
          <option value="active">{STATUS_LABELS.active}</option>
          <option value="do_not_contact">{STATUS_LABELS.do_not_contact}</option>
          <option value="not_a_fit">{STATUS_LABELS.not_a_fit}</option>
          <option value="inactive">{STATUS_LABELS.inactive}</option>
        </select>
        <select
          value={linkedinFilter}
          onChange={(e) => setLinkedinFilter(e.target.value as "" | "yes" | "no")}
          className={`px-3 py-1.5 h-9 ${FORM_CONTROL}`}
        >
          <option value="">LinkedIn: all</option>
          <option value="yes">Has LinkedIn</option>
          <option value="no">No LinkedIn</option>
        </select>
        <select
          value={countryFilter}
          onChange={(e) => setCountryFilter(e.target.value)}
          className={`px-3 py-1.5 h-9 ${FORM_CONTROL}`}
        >
          <option value="">Office country: any</option>
          <option value="none">Not set</option>
          {countryOptions.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
        <select
          value={stateFilter}
          onChange={(e) => setStateFilter(e.target.value)}
          className={`px-3 py-1.5 h-9 ${FORM_CONTROL}`}
          title="Companies with at least one office in this state"
        >
          <option value="">Office state: any</option>
          {stateOptions.map((st) => (
            <option key={st} value={st}>{st}</option>
          ))}
        </select>
        <label className="flex items-center gap-1.5 text-sm text-foreground cursor-pointer" title="Only companies added by hand with the Add company button">
          <input
            type="checkbox"
            checked={manuallyAdded}
            onChange={(e) => setManuallyAdded(e.target.checked)}
            className="accent-primary"
          />
          Manually added
        </label>
        <label className="flex items-center gap-1.5 text-sm text-foreground cursor-pointer" title="Only companies where somebody replied on an RFP (shows companies that were never a prime too)">
          <input
            type="checkbox"
            checked={hasReplied}
            onChange={(e) => setHasReplied(e.target.checked)}
            className="accent-primary"
          />
          Has replied
        </label>
        <label className="flex items-center gap-1.5 text-sm text-foreground cursor-pointer" title="Only companies whose role (prime or sub) the sheet did not say on at least one RFP and nobody has decided yet">
          <input
            type="checkbox"
            checked={unconfirmed}
            onChange={(e) => setUnconfirmed(e.target.checked)}
            className="accent-primary"
          />
          Role unconfirmed
        </label>
        <label className="flex items-center gap-1.5 text-sm text-foreground cursor-pointer" title="Only companies with no link to an active RFP, such as one added by hand or kept after its last RFP was deleted">
          <input
            type="checkbox"
            checked={noRfp}
            onChange={(e) => setNoRfp(e.target.checked)}
            className="accent-primary"
          />
          No RFP linked
        </label>
        <label className="flex items-center gap-1.5 text-sm text-foreground cursor-pointer">
          <input
            type="checkbox"
            checked={missingAddress}
            onChange={(e) => setMissingAddress(e.target.checked)}
            className="accent-primary"
          />
          No address set
        </label>
        <label className="flex items-center gap-1.5 text-sm text-foreground cursor-pointer" title="Has a city or zip on file that could not be placed on the map">
          <input
            type="checkbox"
            checked={officeNotLocated}
            onChange={(e) => setOfficeNotLocated(e.target.checked)}
            className="accent-primary"
          />
          Office not located
        </label>
        <label className="flex items-center gap-1.5 text-sm text-foreground cursor-pointer" title="Nobody has researched what the company does yet">
          <input
            type="checkbox"
            checked={serviceMissing}
            onChange={(e) => setServiceMissing(e.target.checked)}
            className="accent-primary"
          />
          Service categories missing
        </label>
        <label className="flex items-center gap-1.5 text-sm text-foreground cursor-pointer" title="No capability statement on file">
          <input
            type="checkbox"
            checked={capabilityMissing}
            onChange={(e) => setCapabilityMissing(e.target.checked)}
            className="accent-primary"
          />
          Capability statement missing
        </label>
        <label className="flex items-center gap-1.5 text-sm text-foreground cursor-pointer" title="No past-performance entry on file and nobody has marked it researched">
          <input
            type="checkbox"
            checked={pastPerformanceMissing}
            onChange={(e) => setPastPerformanceMissing(e.target.checked)}
            className="accent-primary"
          />
          Past performance missing
        </label>
        <label className="flex items-center gap-1.5 text-sm text-foreground cursor-pointer" title="Nobody has researched this company's licences and certificates yet">
          <input
            type="checkbox"
            checked={credentialsMissing}
            onChange={(e) => setCredentialsMissing(e.target.checked)}
            className="accent-primary"
          />
          Credentials missing
        </label>
        <label className="flex items-center gap-1.5 text-sm text-foreground cursor-pointer" title="Companies with NAICS suggestions waiting for approval">
          <input
            type="checkbox"
            checked={naicsPending}
            onChange={(e) => setNaicsPending(e.target.checked)}
            className="accent-primary"
          />
          NAICS needs approval
        </label>
        <input
          value={serviceInput}
          onChange={(e) => setServiceInput(e.target.value)}
          placeholder="Service contains…"
          aria-label="Service contains"
          className={`w-44 px-3 py-1.5 h-9 ${FILLED_INPUT}`}
        />
        <input
          value={credentialInput}
          onChange={(e) => setCredentialInput(e.target.value)}
          placeholder="Credential contains…"
          aria-label="Credential contains"
          className={`w-44 px-3 py-1.5 h-9 ${FILLED_INPUT}`}
        />
        <div className="ml-auto">
          <SortMenu
            options={[{ key: "recent_rfp_appearance", label: "Recent RFP Appearance" }]}
            sortBy={sortBy}
            sortDir={sortDir}
            onChange={(by, dir) => { setSortBy(by); setSortDir(dir); }}
          />
        </div>
      </div>

      {error && <Alert tone="danger">{error}</Alert>}

      <div className={`${TABLE_SHELL} overflow-x-auto flex-1 min-h-0`}>
        <table className="w-full min-w-[1000px]">
          <thead>
            <tr className={THEAD_ROW}>
              <th className={TH}>Company</th>
              <th className={TH}>Role on RFPs</th>
              <th className={TH}>RFPs</th>
              <th className={TH}>Outreach</th>
              <th className={TH}>LinkedIn</th>
              <th className={TH}>NAICS</th>
              <th className={TH}>Offices</th>
            </tr>
          </thead>
          <tbody>
            {items.map((c) => (
              <tr key={c.id} id={rowDomId("company", c.id)} className={`${TBODY_ROW} ${highlightId === c.id ? ROW_HIGHLIGHT_CLASS : ""}`}>
                <td className={TD}>
                  <div className="flex items-center gap-2">
                    <Link
                      to={`${crmBasePath}/companies/${c.id}`}
                      state={rowLinkState(location)}
                      className="flex items-center gap-2 text-sm text-primary hover:underline"
                    >
                      <Building2 className="w-3.5 h-3.5 flex-shrink-0" />
                      {c.name || "(unnamed)"}
                    </Link>
                    <StatusBadge status={c.status} />
                    {(c.reply_count ?? 0) > 0 && (
                      <HoverCard
                        accent="border-success-border"
                        trigger={<MessageSquare aria-label="Somebody here replied" className="w-3.5 h-3.5 text-success-soft-foreground fill-success/20 flex-shrink-0" />}
                      >
                        <span className="flex flex-col gap-1">
                          <span className="text-[11px] font-semibold uppercase tracking-wide text-success-soft-foreground">Replied</span>
                          <span className="text-xs text-foreground">
                            Somebody here replied on {c.reply_count} RFP{c.reply_count === 1 ? "" : "s"}
                            {c.latest_reply_at ? `, latest ${formatDate(c.latest_reply_at, true)}` : ""}.
                          </span>
                          <span className="text-[11px] text-muted-foreground italic">Open the company for the RFPs and the replies.</span>
                        </span>
                      </HoverCard>
                    )}
                    {c.created_manually && <StatusPill tone="info" size="sm" title="Added by hand with the Add company button">Added manually</StatusPill>}
                  </div>
                  {c.domain && <div className="pl-[22px] text-xs text-muted-foreground">{c.domain}</div>}
                </td>
                <td className={`${TD} text-sm text-foreground tabular-nums whitespace-nowrap`}>
                  <div>Prime on {c.prime_rfp_count} RFP{c.prime_rfp_count === 1 ? "" : "s"}</div>
                  {c.unconfirmed_rfp_count > 0 && (
                    <div className="text-xs text-warning-soft-foreground" title="RFPs where the sheet did not say whether this company is a prime; a person decides before any email is drafted">
                      Role unconfirmed on {c.unconfirmed_rfp_count} RFP{c.unconfirmed_rfp_count === 1 ? "" : "s"}
                    </div>
                  )}
                  {c.sub_appearance_count > 0 && (
                    <div className="text-xs text-muted-foreground" title="RFPs where this prime appeared only as a sub-contractor (recorded, not linked)">
                      Also a sub on {c.sub_appearance_count} RFP{c.sub_appearance_count === 1 ? "" : "s"}
                    </div>
                  )}
                </td>
                <td className={`${TD} text-sm text-foreground tabular-nums`}>
                  {c.rfp_count}
                  {c.last_rfp_appearance_at && (
                    <div className="text-xs text-muted-foreground whitespace-nowrap">Last: {formatDate(c.last_rfp_appearance_at, true)}</div>
                  )}
                </td>
                <td className={`${TD} text-sm`}><CompanyOutreachCell outreach={c.outreach} historyCount={c.outreach_history_count} /></td>
                <td className={`${TD} text-sm`}>
                  {c.linkedin_url ? (
                    <Tooltip
                      align="left"
                      content={
                        isLinkedinSearchUrl(c.linkedin_url) ? (
                          <>
                            <span className="block font-medium">Search page, not the actual company page</span>
                            <span className="block opacity-70 break-all mt-0.5">{c.linkedin_url}</span>
                          </>
                        ) : (
                          <span className="break-all">{c.linkedin_url}</span>
                        )
                      }
                    >
                      <a
                        href={c.linkedin_url}
                        target="_blank"
                        rel="noreferrer"
                        aria-label="Open LinkedIn"
                        className={`inline-flex items-center gap-1 ${
                          isLinkedinSearchUrl(c.linkedin_url) ? "text-warning-soft-foreground hover:text-warning" : "text-info hover:text-info/80"
                        }`}
                      >
                        <Linkedin className="w-4 h-4" />
                        {isLinkedinSearchUrl(c.linkedin_url) && <TriangleAlert className="w-3 h-3" />}
                      </a>
                    </Tooltip>
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </td>
                <td className={`${TD} text-sm font-mono text-foreground`}>
                  {c.naics_codes.length > 0 ? c.naics_codes.join(", ") : <span className="text-muted-foreground">not tagged</span>}
                </td>
                <td className={`${TD} text-sm text-foreground`}>
                  {c.countries.length > 0 ? c.countries.join(", ") : "—"}
                  {c.locations.length > 0 && (
                    <div className="text-xs text-muted-foreground">{c.locations.length} office{c.locations.length === 1 ? "" : "s"}</div>
                  )}
                </td>
              </tr>
            ))}
            {!loading && items.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-sm text-muted-foreground">
                  {manuallyAdded && !search && !status && !countryFilter && !stateFilter
                    ? "No companies have been added by hand yet — use Add company."
                    : "No companies match these filters."}
                </td>
              </tr>
            )}
          </tbody>
        </table>
        {loading && (
          <div className="flex items-center justify-center py-10">
            <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        )}
      </div>

      <PagerNav page={page} totalPages={totalPages} onChange={setPage} />
    </div>
  );
}
